import { authApiActions } from './auth.actions';
import { IAuthenticatedUser, IUser } from './user.entity';
import { updatedAuth0User } from '@core/auth/auth.effects';
import { createReducer, on } from '@ngrx/store';
import { AccountDeletionError } from './account-deletion-error';

export interface IAuthError {
  message: string;
  object?: any;
}

export interface ISignUpError {
  success: false;
  error: string;
  statusCode: number;
}

export interface ISignupSuccess {
  success: true;
  userId: string | null;
  identityUserId: string | null;
  claimPending: boolean;
  email: string;
}

export interface IAuthAppState {
  target?: string;
  managementToken?: string;
}

export type AccountManagementStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface State {
  user: IAuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticating: boolean;
  error: IAuthError | null;
  impersonating: IUser | null;
  signupPending: boolean;
  signupResult: ISignupSuccess | ISignUpError | null;
  state: IAuthAppState | null;
  idToken: string | null;
  managementAuthToken: string | null;
  accountManagementStatus: AccountManagementStatus;
  accountDeletionError: AccountDeletionError | null;
  // True from the moment a deactivation/deletion request is dispatched until
  // it resolves (success or failure). The effect's `exhaustMap` silently
  // drops a repeat dispatch while a request is in flight, so the template
  // disables the submit control on this flag to avoid the appearance of a
  // dropped click. Deliberately NOT cleared by `clearAccountDeletionError`
  // (dispatched unconditionally on every page mount): the in-flight request
  // survives component teardown/navigation, so clearing it on return would
  // let the user fire a second, silently-dropped request.
  accountDeletionInFlight: boolean;
}

export const initialAuthState: State = {
  user: null,
  isLoading: false,
  isAuthenticating: false,
  error: null,
  impersonating: null,
  signupResult: null,
  signupPending: false,
  state: null,
  idToken: null,
  managementAuthToken: null,
  accountManagementStatus: 'idle',
  accountDeletionError: null,
  accountDeletionInFlight: false,
};

export const authReducer = createReducer(
  initialAuthState,
  on(
    authApiActions.deleteAccount,
    authApiActions.deactivateAccount,
    (state): State => ({
      ...state,
      accountDeletionError: null,
      accountDeletionInFlight: true,
    })
  ),
  on(
    authApiActions.accountDeleted,
    authApiActions.accountDeactivated,
    (state): State => ({
      ...state,
      accountDeletionError: null,
      accountDeletionInFlight: false,
    })
  ),
  on(
    authApiActions.clearAccountDeletionError,
    (state): State => ({
      ...state,
      accountDeletionError: null,
    })
  ),
  on(
    authApiActions.accountDeletionFailed,
    authApiActions.accountDeactivationFailed,
    (state, { reason }): State => ({
      ...state,
      accountDeletionError: reason,
      accountDeletionInFlight: false,
    })
  ),
  on(authApiActions.startSignUp, (state, props) => ({
    ...state,
    signupResult: null,
    signupPending: true,
  })),
  on(authApiActions.userSignUpFailed, (state, props) => ({
    ...state,
    signupPending: false,
    signupResult: {
      success: false as const,
      error: props.error,
      statusCode: props.statusCode,
    },
  })),
  on(authApiActions.userSignUpSucceeded, (state, props) => ({
    ...state,
    signupResult: {
      success: true as const,
      userId: props.userId,
      identityUserId: props.identityUserId,
      claimPending: props.claimPending,
      email: props.email,
    },
    signupPending: false,
  })),
  on(authApiActions.loadingUser, (state, props) => ({
    ...state,
    isLoading: true,
    isAuthenticating: false,
    error: null,
  })),
  on(authApiActions.startRegistrationRedirect, (state, props) => ({
    ...state,
    user: null,
    isLoading: true,
    isAuthenticating: false,
    error: null,
    impersonating: null,
  })),
  on(authApiActions.idTokenReceived, (state, props): State => {
    // idTokenReceived also fires on silent token refresh. The refreshed
    // claims never carry linked identities (createUserFromClaims always
    // sets `identities: []`), so blindly taking the incoming user would
    // discard the loaded projection and force a refetch. Keep the existing
    // projection while the subject is unchanged; a different subject is a
    // different account and must start from `idle`.
    const sameSubject =
      state.user !== null && state.user.sub === props.user.sub;
    return {
      ...state,
      user: sameSubject
        ? { ...props.user, identities: state.user?.identities ?? [] }
        : props.user,
      impersonating: props.impersonating,
      isLoading: false,
      isAuthenticating: false,
      error: null,
      idToken: props.idToken,
      accountManagementStatus: sameSubject
        ? state.accountManagementStatus
        : ('idle' as const),
      accountDeletionError: sameSubject ? state.accountDeletionError : null,
      accountDeletionInFlight: sameSubject
        ? state.accountDeletionInFlight
        : false,
    };
  }),
  on(
    authApiActions.loadAccountManagement,
    (state): State => ({
      ...state,
      accountManagementStatus: 'loading' as const,
    })
  ),
  on(
    authApiActions.accountManagementReceived,
    (state, props): State => ({
      ...state,
      user: state.user
        ? {
            ...state.user,
            identities: props.linkedIdentities.map((identity) => ({
              provider: identity.provider,
              user_id: identity.externalAccountId ?? '',
              connected_at: identity.linkedAt ?? '',
              picture: identity.pictureUrl ?? '',
            })),
          }
        : state.user,
      accountManagementStatus: state.user ? 'loaded' : 'idle',
    })
  ),
  on(
    authApiActions.accountManagementFailed,
    (state): State => ({
      ...state,
      accountManagementStatus: state.user
        ? ('error' as const)
        : ('idle' as const),
    })
  ),
  on(authApiActions.startUserImpersonation, (state, props) => ({
    ...state,
    impersonating: props.userToImpersonate,
  })),
  on(authApiActions.stopUserImpersonation, (state, props) => ({
    ...state,
    impersonating: null,
  })),
  on(authApiActions.startSignIn, (state, props) => ({
    ...state,
    user: null,
    isLoading: true,
    isAuthenticating: true,
    error: null,
    impersonating: null,
  })),
  on(
    authApiActions.signOut,
    authApiActions.signedOutByIdentityProvider,
    (state, props) => ({
      ...state,
      user: null,
      isAuthenticating: false,
      isLoading: false,
      error: null,
      impersonating: null,
      idToken: null,
      state: null,
      accountManagementStatus: 'idle' as const,
      accountDeletionError: null,
      accountDeletionInFlight: false,
    })
  ),
  on(authApiActions.loadingUserComplete, (state, props) => ({
    ...state,
    isLoading: false,
    isAuthenticating: false,
    impersonating: null,
  })),
  on(authApiActions.authenticationError, (state, props) => ({
    ...state,
    isLoading: false,
    isAuthenticating: false,
    error: props.handled
      ? null
      : {
          message: props.message,
          object: props.object,
        },
  })),
  on(authApiActions.authStateReceived, (state, props) => ({
    ...state,
    state: props.appState,
  })),
  on(authApiActions.authStateProcessed, (state, props) => ({
    ...state,
    state: null,
  })),
  on(authApiActions.managementAccessTokenReceived, (state, props) => ({
    ...state,
    managementAuthToken: props.token,
  })),

  on(
    authApiActions.profilePictureUpdated,
    authApiActions.profileEmailVerifiedAndAdded,
    authApiActions.profileNameUpdated,
    (state, props) => ({
      ...state,
      user: state.user ? updatedAuth0User(props, state.user) : state.user,
    })
  )
);
